import React, { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { toast } from '@/design-system/toaster';

import {
  parseWarehouseError,
  splitRetryable,
  toShortfall,
  warehouseApi,
} from '../api/warehouse.api';
import type { OutboundOrder, ShortfallRow } from '../types';
import { fieldErrorsOf } from '../hooks/useServerErrors';
import { ActionDialog, ReasonModal, ShortfallModal } from './dialogs';
import { WAREHOUSE_PATHS } from '../paths';

/**
 * Outbound-order actions shared by the list and the detail page: create a
 * picking order (single / bulk, INSUFFICIENT_STOCK → shortfall dialog) and
 * cancel with a mandatory reason, each behind its confirm dialog
 * (`requestPicking` / `requestBulkPicking` / `requestCancel` /
 * `requestBulkCancel`). Render `dialogs` once in the page.
 *
 * Cancel is for orders that are still in process (Open / Preparing / Ready to
 * pick up): the reservation is released. Once an order is shipped there is no
 * cancel here — "Batal submit shipping" (`useUndoShipping`) goes back while the
 * goods are still at the hub. A refusal the user can act on (409) stays in the
 * dialog.
 *
 * Each action runs at most once at a time (a second click while one is in
 * flight is ignored). After a mutation `onChanged` reloads whatever the
 * page shows.
 */
export function useOutboundActions(onChanged: () => void) {
  const navigate = useNavigate();
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  const pickingRef = useRef(new Set<string>());
  const [busyIDs, setBusyIDs] = useState<ReadonlySet<string>>(new Set());
  const bulkRef = useRef(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [shortfall, setShortfall] = useState<{
    code: string;
    rows: ShortfallRow[];
    message: string;
  } | null>(null);
  const [cancelTarget, setCancelTarget] = useState<OutboundOrder | null>(null);
  const cancelRef = useRef(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  /** Server validation message for the cancel reason. */
  const [cancelError, setCancelError] = useState<string | null>(null);
  /** Orders waiting for the bulk Cancel confirm. */
  const [bulkCancelTarget, setBulkCancelTarget] = useState<
    OutboundOrder[] | null
  >(null);
  /** Orders waiting for the Create picking order confirm. */
  const [pickTarget, setPickTarget] = useState<OutboundOrder[] | null>(null);

  const markBusy = useCallback((id: string, on: boolean) => {
    if (on) pickingRef.current.add(id);
    else pickingRef.current.delete(id);
    setBusyIDs(new Set(pickingRef.current));
  }, []);

  const createPicking = useCallback(
    async (order: OutboundOrder) => {
      if (pickingRef.current.has(order.id)) return;
      markBusy(order.id, true);
      try {
        const { detail } = await warehouseApi.createPickingOrder(order.id);
        toast.success('Picking order dibuat', {
          description: detail.goodsIssue.code,
        });
        navigate(`${WAREHOUSE_PATHS.picking}/${detail.goodsIssue.id}`);
      } catch (err) {
        const e = parseWarehouseError(err);
        if (e.code === 'INSUFFICIENT_STOCK') {
          setShortfall({
            code: order.code,
            rows: e.shortfall,
            message: e.message,
          });
        } else {
          toast.error('Gagal membuat picking order', {
            description: e.message,
          });
        }
        onChangedRef.current();
      } finally {
        markBusy(order.id, false);
      }
    },
    [markBusy, navigate]
  );

  const bulkCreatePicking = useCallback(async (orders: OutboundOrder[]) => {
    if (!orders.length || bulkRef.current) return;
    bulkRef.current = true;
    setBulkBusy(true);
    try {
      const { results } = await warehouseApi.bulkCreatePickingOrders(
        orders.map((o) => o.id)
      );
      const ok = results.filter((r) => r.ok);
      const { retryable, others: failed } = splitRetryable(
        results.filter((r) => !r.ok)
      );
      const codeOf = (id: string) =>
        orders.find((o) => o.id === id)?.code ?? id;
      if (ok.length) {
        toast.success(`${ok.length} picking order dibuat`, {
          description: ok
            .map((r) => r.goodsIssueCode || codeOf(r.outboundOrderID))
            .slice(0, 3)
            .join(', '),
        });
      }
      const firstStock = failed.find(
        (r) => r.error?.error === 'INSUFFICIENT_STOCK'
      );
      if (firstStock) {
        setShortfall({
          code:
            codeOf(firstStock.outboundOrderID) +
            (failed.length > 1
              ? ` (+${failed.length - 1} order lain gagal)`
              : ''),
          rows: toShortfall(firstStock.error?.errors),
          message: firstStock.error?.message ?? 'Stok tidak cukup',
        });
      } else if (failed.length) {
        toast.warning(`${failed.length} order gagal dibuat picking-nya`, {
          description: failed
            .slice(0, 3)
            .map(
              (f) =>
                `${codeOf(f.outboundOrderID)}: ${f.error?.message ?? 'gagal'}`
            )
            .join(' · '),
        });
      }
      if (retryable.length) {
        // Transient lock / deadlock: nothing changed for these, safe to resend.
        const again = orders.filter((o) =>
          retryable.some((r) => r.outboundOrderID === o.id)
        );
        toast.warning(`${retryable.length} order bentrok dengan proses lain`, {
          description: `${again
            .slice(0, 3)
            .map((o) => o.code)
            .join(', ')} — aman untuk dicoba lagi.`,
          action: {
            label: 'Coba lagi',
            onClick: () => void bulkRetryRef.current(again),
          },
        });
      }
    } catch (err) {
      toast.error('Gagal membuat picking order', {
        description: parseWarehouseError(err).message,
      });
    } finally {
      bulkRef.current = false;
      setBulkBusy(false);
      onChangedRef.current();
    }
  }, []);
  const bulkRetryRef = useRef(bulkCreatePicking);
  bulkRetryRef.current = bulkCreatePicking;

  const confirmCancel = async (reason: string) => {
    if (!cancelTarget || cancelRef.current) return;
    cancelRef.current = true;
    setCancelBusy(true);
    setCancelError(null);
    try {
      const res = await warehouseApi.cancelOutboundOrder(
        cancelTarget.id,
        reason
      );
      toast.info('Outbound request dibatalkan', {
        description: res?.releasedGoodsIssueCode
          ? `Reserve ${res.releasedGoodsIssueCode} dilepas.`
          : cancelTarget.code,
      });
      setCancelTarget(null);
      onChangedRef.current();
    } catch (err) {
      const reasonErr = fieldErrorsOf(err).list.find(
        (x) => x.field === 'reason'
      );
      const e = parseWarehouseError(err);
      if (reasonErr) setCancelError(reasonErr.message);
      // A refusal with a next step: keep it in front of the user, in the dialog.
      else if (e.status === 409) setCancelError(e.message);
      else
        toast.error('Gagal membatalkan order', { description: e.message });
    } finally {
      cancelRef.current = false;
      setCancelBusy(false);
    }
  };

  const requestCancel = useCallback((order: OutboundOrder) => {
    setCancelError(null);
    setCancelTarget(order);
  }, []);
  const requestBulkCancel = useCallback((orders: OutboundOrder[]) => {
    setCancelError(null);
    if (orders.length) setBulkCancelTarget(orders);
  }, []);

  /** One reason for the selection, one request per order (like eFishery). */
  const confirmBulkCancel = async (reason: string) => {
    const orders = bulkCancelTarget ?? [];
    if (!orders.length || cancelRef.current) return;
    cancelRef.current = true;
    setCancelBusy(true);
    setCancelError(null);
    const failed: string[] = [];
    let done = 0;
    for (const o of orders) {
      try {
        await warehouseApi.cancelOutboundOrder(o.id, reason);
        done += 1;
      } catch (err) {
        const reasonErr = fieldErrorsOf(err).list.find(
          (x) => x.field === 'reason'
        );
        if (reasonErr) {
          setCancelError(reasonErr.message);
          break;
        }
        failed.push(`${o.code}: ${parseWarehouseError(err).message}`);
      }
    }
    cancelRef.current = false;
    setCancelBusy(false);
    if (done === 0 && !failed.length) return; // reason refused: dialog stays
    setBulkCancelTarget(null);
    if (failed.length) {
      toast.error(`${done} dibatalkan, ${failed.length} gagal`, {
        description: failed.join(' · '),
      });
    } else {
      toast.info(`${done} outbound request dibatalkan`);
    }
    onChangedRef.current();
  };
  const requestPicking = useCallback(
    (order: OutboundOrder) => setPickTarget([order]),
    []
  );
  const requestBulkPicking = useCallback((orders: OutboundOrder[]) => {
    if (orders.length) setPickTarget(orders);
  }, []);

  const confirmPicking = async () => {
    const orders = pickTarget ?? [];
    if (orders.length === 1) await createPicking(orders[0]);
    else await bulkCreatePicking(orders);
    setPickTarget(null);
  };
  const pickCount = pickTarget?.length ?? 0;

  const cancelNotice =
    cancelTarget?.status === 'SHIPPED'
      ? `Shipment${
          cancelTarget.fulfillment?.waybill
            ? ` ${cancelTarget.fulfillment.waybill}`
            : ''
        } dibatalkan dan stok yang sudah di-ship kembali ke lokasi asal (on hand). Hanya selama barang masih di hub.`
      : cancelTarget?.goodsIssue
        ? 'Order dibatalkan dan reserve stok dari picking order dilepas.'
        : 'Order dibatalkan.';

  const dialogs = (
    <>
      <ActionDialog
        open={!!pickTarget}
        title="Create picking order"
        subtitle={
          pickCount === 1 && pickTarget
            ? `${pickTarget[0].code} · ${pickTarget[0].externalID}`
            : `${pickCount} order dipilih`
        }
        description="Stok di-reserve dan order pindah ke Preparing."
        confirmLabel={
          pickCount > 1
            ? `Create picking order (${pickCount})`
            : 'Create picking order'
        }
        icon="ri-file-list-3-line"
        busy={
          pickCount === 1 && pickTarget
            ? busyIDs.has(pickTarget[0].id)
            : bulkBusy
        }
        testId="outbound-picking-dialog"
        onClose={() => setPickTarget(null)}
        onConfirm={() => void confirmPicking()}
      />
      <ReasonModal
        open={!!cancelTarget}
        title="Cancel order"
        subtitle={
          cancelTarget
            ? `${cancelTarget.code} · ${cancelTarget.externalID}`
            : undefined
        }
        notice={cancelNotice}
        confirmLabel="Cancel order"
        busy={cancelBusy}
        error={cancelError}
        testId="outbound-cancel-dialog"
        onConfirm={confirmCancel}
        onClose={() => setCancelTarget(null)}
      />
      <ReasonModal
        open={!!bulkCancelTarget}
        title="Cancel order"
        subtitle={`${bulkCancelTarget?.length ?? 0} order dipilih`}
        notice="Order dibatalkan dan reserve stok dari picking order-nya dilepas. Satu alasan untuk semua."
        confirmLabel={`Cancel order (${bulkCancelTarget?.length ?? 0})`}
        busy={cancelBusy}
        error={cancelError}
        testId="outbound-bulk-cancel-dialog"
        onConfirm={confirmBulkCancel}
        onClose={() => setBulkCancelTarget(null)}
      />
      <ShortfallModal
        open={!!shortfall}
        orderCode={shortfall?.code ?? ''}
        rows={shortfall?.rows ?? []}
        message={shortfall?.message}
        onClose={() => setShortfall(null)}
        onOpenInbound={() => {
          setShortfall(null);
          navigate(WAREHOUSE_PATHS.inboundRequest);
        }}
      />
    </>
  );

  const isBusy = useCallback((id: string) => busyIDs.has(id), [busyIDs]);

  return {
    createPicking,
    bulkCreatePicking,
    /** Ask, then create a picking order for one order. */
    requestPicking,
    /** Ask once, then create picking orders for the selection. */
    requestBulkPicking,
    requestCancel,
    /** Ask once (one reason), then cancel each selected order. */
    requestBulkCancel,
    /** A picking order is being created for this order (stable callback). */
    isBusy,
    bulkBusy,
    dialogs,
  };
}
